import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["currencyCode", "currencySymbol", "feePercentage", "feeFixed", "token", "tokenError", "tokenSuccess", "tokenLoading", "submitButton", "name"];

  connect() {
    // Set initial currency symbol on connect
    this.updateCurrencySymbol();
    
    // Auto-fill stripe fees with default values
    this.autoFillStripeFees();
    
    // Initialize form validation
    this.tokenInUse = false;
    this.validateForm();
  }

  updateCurrencySymbol(event) {
    const currencyCode = this.currencyCodeTarget.value;
    
    if (currencyCode === "USD") {
      this.currencySymbolTarget.value = "$";
    } else if (currencyCode === "GBP") {
      this.currencySymbolTarget.value = "£";
    }
  }

  autoFillStripeFees() {
    // Set default fee percentage
    if (this.hasFeePercentageTarget) {
      this.feePercentageTarget.value = "2.9";
    }
    
    // Set default fixed fee
    if (this.hasFeeFixedTarget) {
      this.feeFixedTarget.value = "30";
    }
  }

  validateForm() {
    if (!this.hasSubmitButtonTarget) return;

    const name = this.nameTarget.value?.trim();
    const nameIsEmpty = !name || name === "";
    
    // Disable if name is empty or token is in use
    if (nameIsEmpty || this.tokenInUse) {
      this.submitButtonTarget.disabled = true;
    } else {
      this.submitButtonTarget.disabled = false;
    }
  }

  tokenCheckTimeout = null;
  tokenCheckIdForThrottling = null;

  checkTokenAvailability() {
    // Clear existing timeout
    if (this.tokenCheckTimeout) {
      clearTimeout(this.tokenCheckTimeout);
    }

    const token = this.tokenTarget.value?.trim();

    // Hide messages if token is empty
    if (!token || token === "") {
      this.hideTokenError();
      this.hideTokenSuccess();
      this.hideTokenLoading();
      this.tokenInUse = false;
      this.validateForm();
      return;
    }

    // Debounce the check - wait 1 second after user stops typing
    this.tokenCheckTimeout = setTimeout(() => {
      this.performTokenCheck(token);
    }, 1000);
  }

  async performTokenCheck(token) {
    const randomId = Math.random().toString(36).substring(7);
    this.tokenCheckIdForThrottling = randomId;

    // Show loading state
    this.showTokenLoading();
    this.hideTokenError();
    this.hideTokenSuccess();

    try {
      // Check both endpoints in parallel
      const [usResponse, sResponse] = await Promise.all([
        fetch(`/us/${token}`, { method: 'HEAD' }),
        fetch(`/s/${token}`, { method: 'HEAD' })
      ]);

      // If the check ID has changed, another check has been initiated, ignore this result
      if (this.tokenCheckIdForThrottling !== randomId) return;

      // Hide loading state
      this.hideTokenLoading();

      // If either endpoint returns 200 (OK), the token is already taken
      if (usResponse.ok || sResponse.ok) {
        this.showTokenError("This token is already in use. Please choose a different token.");
        this.hideTokenSuccess();
        this.tokenInUse = true;
      } else {
        // Both returned non-200 (likely 404), token is available
        this.hideTokenError();
        this.showTokenSuccess("This token is available.");
        this.tokenInUse = false;
      }
      
      // Validate form after token check
      this.validateForm();
    } catch (error) {
      // If the check ID has changed, ignore this result
      if (this.tokenCheckIdForThrottling !== randomId) return;

      // Hide loading state
      this.hideTokenLoading();

      // On network errors, we can't determine availability, so don't show messages
      console.error("Error checking token availability:", error);
      this.hideTokenError();
      this.hideTokenSuccess();
      this.tokenInUse = false;
      this.validateForm();
    }
  }

  showTokenError(message) {
    if (this.hasTokenErrorTarget) {
      this.tokenErrorTarget.textContent = message;
      this.tokenErrorTarget.style.display = "block";
      this.tokenTarget.classList.add("isInvalid");
    }
  }

  hideTokenError() {
    if (this.hasTokenErrorTarget) {
      this.tokenErrorTarget.style.display = "none";
      this.tokenTarget.classList.remove("isInvalid");
    }
  }

  showTokenSuccess(message) {
    if (this.hasTokenSuccessTarget) {
      this.tokenSuccessTarget.textContent = message;
      this.tokenSuccessTarget.style.display = "block";
    }
  }

  hideTokenSuccess() {
    if (this.hasTokenSuccessTarget) {
      this.tokenSuccessTarget.style.display = "none";
    }
  }

  showTokenLoading() {
    if (this.hasTokenLoadingTarget) {
      this.tokenLoadingTarget.style.display = "block";
    }
  }

  hideTokenLoading() {
    if (this.hasTokenLoadingTarget) {
      this.tokenLoadingTarget.style.display = "none";
    }
  }
}

